/**
 * Shared client-side validation for EduViaOS forms.
 *
 * The backend is the real gate; these schemas exist so a form can block a
 * bad submit and explain the problem next to the field that caused it.
 * Every rule mirrors the corresponding backend constraint — when a backend
 * pattern changes, change the rule here in the same PR.
 *
 * Slugs and keys are identifiers derived from a human name. Users never
 * type them: forms call {@link deriveSlug} on the name and report problems
 * on the name field via {@link slugProblemFor}.
 */
import { z } from "zod";

// ── Free text ──────────────────────────────────────────────────────────

// eslint-disable-next-line no-control-regex -- matching control chars is the point.
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;
const ANGLE_BRACKETS = /[<>]/;

export const NAME_MAX_LENGTH = 120;

/** Build a trimmed, printable, single-line name schema with a custom cap. */
export function boundedNameSchema(max: number = NAME_MAX_LENGTH, label = "Name") {
  return z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .max(max, `${label} must be ${max} characters or fewer.`)
    .refine((v) => !CONTROL_CHARS.test(v), `${label} can't contain line breaks or tabs.`)
    .refine((v) => !ANGLE_BRACKETS.test(v), `${label} can't contain < or >.`);
}

/** Display names: trimmed, 1–120 printable characters, no `<` / `>`. */
export const nameSchema = boundedNameSchema();

// ── Contact details ────────────────────────────────────────────────────

export const EMAIL_MAX_LENGTH = 254;

export const emailSchema = z
  .string()
  .trim()
  .min(1, "Email is required.")
  .max(EMAIL_MAX_LENGTH, `Email must be ${EMAIL_MAX_LENGTH} characters or fewer.`)
  .email("Enter a valid email address, like name@example.com.");

const E164 = /^\+[1-9]\d{7,14}$/;
const INDIAN_MOBILE = /^[6-9]\d{9}$/;

/** Strip spaces, dashes, dots and brackets people paste into phone fields. */
export function normalisePhone(value: string): string {
  return value.replace(/[\s\-().]/g, "");
}

export const PHONE_MAX_LENGTH = 20;

/** E.164 (`+919876543210`) or a 10-digit Indian mobile (`9876543210`). */
export const phoneSchema = z
  .string()
  .trim()
  .min(1, "Phone number is required.")
  .max(PHONE_MAX_LENGTH, "Phone number is too long.")
  .transform(normalisePhone)
  .refine(
    (v) => E164.test(v) || INDIAN_MOBILE.test(v),
    "Enter a 10-digit mobile number or a full number starting with +.",
  );

// ── Numbers ────────────────────────────────────────────────────────────

export const MONEY_RUPEES_MAX = 10_000_000;

/** Rupee amounts: 0 – 1,00,00,000 with at most two decimal places. */
export const moneyRupeesSchema = z.coerce
  .number({ invalid_type_error: "Enter an amount in rupees." })
  .refine((v) => Number.isFinite(v), "Enter an amount in rupees.")
  .refine((v) => v >= 0, "Amount can't be negative.")
  .refine((v) => v <= MONEY_RUPEES_MAX, "Amount can't be more than ₹1,00,00,000.")
  .refine((v) => Math.abs(Math.round(v * 100) - v * 100) < 1e-6, "Use at most two decimal places.");

/** Whole numbers ≥ 1. */
export const positiveIntSchema = z.coerce
  .number({ invalid_type_error: "Enter a whole number." })
  .int("Enter a whole number.")
  .min(1, "Must be at least 1.");

// ── Slugs ──────────────────────────────────────────────────────────────

export interface SlugRule {
  /** Backend pattern the derived value must satisfy. */
  pattern: RegExp;
  minLength: number;
  maxLength: number;
  /** Separator used between words. */
  separator: "-" | "_";
  /**
   * Prefix added when the derived value would not start with a letter
   * (only for rules whose pattern requires a leading letter).
   */
  leadingLetterPrefix?: string;
  /** Values the backend refuses outright (platform subdomains etc.). */
  reserved?: ReadonlySet<string>;
}

/**
 * Platform subdomains that can never be a tenant slug. Superset of the
 * backend `RESERVED_SUBDOMAINS` (tenant middleware) and the onboarding
 * reserved-slug list — keep all three in sync.
 */
export const RESERVED_TENANT_SLUGS: ReadonlySet<string> = new Set([
  "www",
  "api",
  "admin",
  "static",
  "assets",
  "cdn",
  "status",
  "docs",
  "blog",
  "app",
  "auth",
  "id",
  "stage",
  "sa",
  "ia",
  "portal",
  "apply",
  "mail",
  "_super_admin",
]);

export const SLUG_RULES = {
  /** `seating/schemas.py` ZoneCreate.slug */
  zone: { pattern: /^[a-z0-9][a-z0-9-]*$/, minLength: 1, maxLength: 60, separator: "-" },
  /** `seating/models.py` _SEAT_SLOT_KEY */
  seatSlotKey: {
    pattern: /^[a-z0-9][a-z0-9-]{0,39}$/,
    minLength: 1,
    maxLength: 40,
    separator: "-",
  },
  /** `forms/schemas.py` FormCreate.slug */
  formSlug: {
    pattern: /^[a-z][a-z0-9_]*$/,
    minLength: 2,
    maxLength: 80,
    separator: "_",
    leadingLetterPrefix: "form_",
  },
  /** `forms/validators.py` snake_re for field keys */
  formFieldKey: {
    pattern: /^[a-z][a-z0-9_]*$/,
    minLength: 1,
    maxLength: 64,
    separator: "_",
    leadingLetterPrefix: "field_",
  },
  /** Assessment topic + template slugs */
  assessment: { pattern: /^[a-z0-9][a-z0-9-]*$/, minLength: 1, maxLength: 80, separator: "-" },
  /** `onboarding/schemas.py` _SLUG_PATTERN */
  tenant: {
    pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    minLength: 2,
    maxLength: 63,
    separator: "-",
    reserved: RESERVED_TENANT_SLUGS,
  },
} as const satisfies Record<string, SlugRule>;

export type SlugRuleName = keyof typeof SLUG_RULES;

function trimSeparators(value: string, sep: string): string {
  const escaped = sep === "-" ? "\\-" : "_";
  return value.replace(new RegExp(`^${escaped}+|${escaped}+$`, "g"), "");
}

/**
 * Derive an identifier from a human name so it satisfies `rule`.
 * Returns `""` when the name has no letters or digits to work with.
 *
 *   deriveSlug("Reading Room 2", SLUG_RULES.zone)      → "reading-room-2"
 *   deriveSlug("Café Élan", SLUG_RULES.assessment)     → "cafe-elan"
 *   deriveSlug("2026 Intake", SLUG_RULES.formSlug)     → "form_2026_intake"
 */
export function deriveSlug(name: string, rule: SlugRule): string {
  const sep = rule.separator;
  let base = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, sep);
  base = trimSeparators(base, sep);
  if (!base) return "";
  if (rule.leadingLetterPrefix && (!/^[a-z]/.test(base) || base.length < rule.minLength)) {
    base = `${rule.leadingLetterPrefix}${base}`;
  }
  return trimSeparators(base.slice(0, rule.maxLength), sep);
}

/**
 * Append `<sep>2`, `<sep>3`, … until `base` no longer collides with
 * `taken`, trimming the base so the suffixed value still fits the rule.
 */
export function uniqueSlug(base: string, taken: Iterable<string>, rule: SlugRule): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; n < 10_000; n += 1) {
    const suffix = `${rule.separator}${n}`;
    const stem = trimSeparators(base.slice(0, rule.maxLength - suffix.length), rule.separator);
    const candidate = `${stem}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  return base;
}

/** Whether `value` satisfies every constraint of `rule`. */
export function isValidSlug(value: string, rule: SlugRule): boolean {
  return (
    value.length >= rule.minLength &&
    value.length <= rule.maxLength &&
    rule.pattern.test(value) &&
    !(rule.reserved?.has(value) ?? false)
  );
}

/** Zod schema for a slug governed by `rule` (for user-editable slugs). */
export function slugSchema(rule: SlugRule = SLUG_RULES.assessment) {
  return z
    .string()
    .trim()
    .min(rule.minLength, `Must be at least ${rule.minLength} characters.`)
    .max(rule.maxLength, `Must be ${rule.maxLength} characters or fewer.`)
    .regex(
      rule.pattern,
      rule.separator === "_"
        ? "Use lowercase letters, numbers and underscores, starting with a letter."
        : "Use lowercase letters, numbers and single hyphens.",
    )
    .refine((v) => !(rule.reserved?.has(v) ?? false), "That address is reserved by EduViaOS.");
}

/**
 * Explain why the identifier derived from `name` can't be used, phrased
 * for the name field (the identifier itself is never shown). Returns
 * `null` when the derived value is usable.
 *
 * @param noun what the name belongs to, e.g. "zone" or "shift".
 */
export function slugProblemFor(
  derived: string,
  rule: SlugRule,
  opts: { noun: string; taken?: Iterable<string> },
): string | null {
  if (!derived) {
    return `Include at least one letter or number in the ${opts.noun} name.`;
  }
  if (rule.reserved?.has(derived)) {
    return `"${derived}" is reserved by EduViaOS. Choose a different ${opts.noun} name.`;
  }
  if (!isValidSlug(derived, rule)) {
    return `Use a longer ${opts.noun} name with letters or numbers.`;
  }
  if (opts.taken) {
    for (const t of opts.taken) {
      if (t === derived) {
        return `Another ${opts.noun} already has this name. Choose a different name.`;
      }
    }
  }
  return null;
}

// ── Helpers ────────────────────────────────────────────────────────────

/** First error message from a zod parse, or `null` when the value is valid. */
export function fieldError(schema: z.ZodTypeAny, value: unknown): string | null {
  const result = schema.safeParse(value);
  return result.success ? null : (result.error.issues[0]?.message ?? "Invalid value.");
}
